import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Globe, Ticket, User, LogOut, Home, Info, Trophy, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import MobileMenu from './MobileMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { currency, setCurrency, EXCHANGE_RATES } = useCurrency();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation links with translations
  const navLinks = [
    { to: '/', labelKey: 'nav.home', icon: Home },
    { to: '/about', labelKey: 'nav.about', icon: Info },
    { to: '/prizes', labelKey: 'nav.prizes', icon: Trophy },
    { to: '/faq', labelKey: 'nav.howToParticipate', icon: HelpCircle }
  ];

  return (
    <nav className="fixed top-0 w-full bg-gradient-to-r from-white via-white to-white/95 backdrop-blur-md border-b border-gray-100 z-50 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-[72px] md:h-[80px]">
          <div className="flex items-center space-x-4">
            <MobileMenu />
            <Link to="/" className="flex items-center -my-2" data-testid="nav-home-link">
              <img 
                src="/logo-marathon-2026.png" 
                alt="Marathon de l'Avenir EDENLIFE 2026" 
                className="h-14 md:h-16 lg:h-[72px] w-auto object-contain"
              />
            </Link>
            
            {/* Main Navigation Menu - Hidden on mobile/tablet, shown on lg+ */}
            <div className="hidden lg:flex items-center space-x-1 ml-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                  data-testid={`nav-link-${link.labelKey}`}
                >
                  <link.icon className="w-4 h-4 mr-1.5" />
                  {t(link.labelKey)}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="language-selector">
                  <Globe className="w-4 h-4 mr-2" />
                  {currentLanguage === 'fr' ? 'FR' : currentLanguage === 'ht' ? 'HT' : currentLanguage === 'es' ? 'ES' : currentLanguage === 'pt' ? 'PT' : 'EN'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => changeLanguage('fr')} data-testid="lang-fr">🇫🇷 Français</DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('en')} data-testid="lang-en">🇬🇧 English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('ht')} data-testid="lang-ht">🇭🇹 Kreyòl</DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('es')} data-testid="lang-es">🇪🇸 Español</DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('pt')} data-testid="lang-pt">🇧🇷 Português</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="currency-selector">
                  {currency}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setCurrency('USD')} data-testid="currency-USD">
                  🇺🇸 USD - Dollar US
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('HTG')} data-testid="currency-HTG">
                  🇭🇹 HTG - Gourde
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('EUR')} data-testid="currency-EUR">
                  🇪🇺 EUR - Euro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('CAD')} data-testid="currency-CAD">
                  🇨🇦 CAD - Dollar Canadien
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('GBP')} data-testid="currency-GBP">
                  🇬🇧 GBP - Livre Sterling
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('DOP')} data-testid="currency-DOP">
                  🇩🇴 DOP - Peso Dominicain
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('CLP')} data-testid="currency-CLP">
                  🇨🇱 CLP - Peso Chilien
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('CHF')} data-testid="currency-CHF">
                  🇨🇭 CHF - Franc Suisse
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency('XOF')} data-testid="currency-XOF">
                  🌍 XOF - Franc CFA
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                <Link to="/purchase" data-testid="nav-buy-tickets">
                  <Button variant="default" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full">
                    <Ticket className="w-4 h-4 mr-2" />
                    {t('nav.buyTickets')}
                  </Button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-5 h-5" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/affiliate')} data-testid="nav-affiliate">
                      {t('nav.affiliate')}
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                        {t('nav.admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-login">
                <Button variant="default" className="bg-primary text-primary-foreground rounded-full">
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;